import type { LogbookEntry } from '@/lib/db';
import { mapColumns, parseCsv, stringifyCsv } from '@/lib/parse/csv';

import { formatIntermediate } from '../columns';
import { blankApproaches, blankLandings, blankTakeoffs, blankTimeMatrix } from '../entry';
import { formatDecimalHours, parseDurationHours } from '../time';
import { intermediateFrom } from './route';
import type { ImportParseResult, ParsedLogbookRow } from './types';

/**
 * ForeFlight's logbook CSV/TSV template.
 *
 * The real template is two tables in one file (an Aircraft table, then a
 * Flights table) plus a "do not delete" marker row — confirmed from
 * ForeFlight's own published Flights-table headers (`Date, AircraftID, From,
 * To, Route, TimeOut, TimeOff, TimeOn, TimeIn, OnDuty, OffDuty, TotalTime,
 * PIC, SIC, Night, Solo, CrossCountry, ..., DayTakeoffs, DayLandingsFullStop,
 * NightTakeoffs, NightLandingsFullStop, AllLandings, ActualInstrument,
 * SimulatedInstrument, ..., DualGiven, DualReceived, ..., PilotComments`).
 * This module deliberately does not try to reproduce that exact preamble,
 * which isn't documented anywhere this app could verify it against a real
 * ForeFlight install (`docs/PHASE-6.md`) — instead it emits a flat CSV using
 * ForeFlight's own confirmed column names, and reads a file back by scanning
 * for the first row that looks like that header rather than assuming line 1
 * is it, so it tolerates a preamble it does not itself write.
 */

const HEADER = [
  'Date',
  'AircraftID',
  'From',
  'To',
  'Route',
  'TotalTime',
  'PIC',
  'SIC',
  'Night',
  'DayTakeoffs',
  'DayLandingsFullStop',
  'NightTakeoffs',
  'NightLandingsFullStop',
  'AllLandings',
  'ActualInstrument',
  'SimulatedInstrument',
  'DualGiven',
  'DualReceived',
  'SimulatedFlight',
  'PilotComments',
] as const;

export function exportForeFlightCsv(entries: readonly LogbookEntry[]): string {
  const rows: (string | number)[][] = [[...HEADER]];
  for (const entry of entries) {
    const dayLandings = entry.landings.day;
    const nightLandings = entry.landings.night;
    rows.push([
      entry.date,
      entry.registration ?? '',
      entry.from ?? '',
      entry.to ?? '',
      formatIntermediate(entry.intermediate),
      formatDecimalHours(entry.time.dayHr + entry.time.nightHr),
      formatDecimalHours(entry.time.picHr),
      formatDecimalHours(entry.time.coPilotHr),
      formatDecimalHours(entry.time.nightHr),
      entry.takeoffs.day,
      dayLandings,
      entry.takeoffs.night,
      nightLandings,
      dayLandings + nightLandings,
      formatDecimalHours(entry.time.actualInstrumentHr),
      formatDecimalHours(entry.time.simulatedInstrumentHr),
      formatDecimalHours(entry.time.instructorHr),
      formatDecimalHours(entry.time.dualHr),
      formatDecimalHours(entry.time.groundInstrumentHr),
      entry.remarks ?? '',
    ]);
  }
  return stringifyCsv(rows);
}

type Field =
  | 'date'
  | 'aircraftId'
  | 'from'
  | 'to'
  | 'route'
  | 'totalTime'
  | 'pic'
  | 'sic'
  | 'night'
  | 'dayTakeoffs'
  | 'dayLandingsFullStop'
  | 'nightTakeoffs'
  | 'nightLandingsFullStop'
  | 'actualInstrument'
  | 'simulatedInstrument'
  | 'dualGiven'
  | 'dualReceived'
  | 'simulatedFlight'
  | 'pilotComments';

const SYNONYMS: Readonly<Record<Field, readonly string[]>> = {
  date: ['date'],
  aircraftId: ['aircraftid', 'aircraft id'],
  from: ['from'],
  to: ['to'],
  route: ['route'],
  totalTime: ['totaltime', 'total time'],
  pic: ['pic'],
  sic: ['sic'],
  night: ['night'],
  dayTakeoffs: ['daytakeoffs', 'day takeoffs'],
  dayLandingsFullStop: ['daylandingsfullstop', 'day landings full stop'],
  nightTakeoffs: ['nighttakeoffs', 'night takeoffs'],
  nightLandingsFullStop: ['nightlandingsfullstop', 'night landings full stop'],
  actualInstrument: ['actualinstrument', 'actual instrument'],
  simulatedInstrument: ['simulatedinstrument', 'simulated instrument'],
  dualGiven: ['dualgiven', 'dual given'],
  dualReceived: ['dualreceived', 'dual received'],
  simulatedFlight: ['simulatedflight', 'simulated flight'],
  pilotComments: ['pilotcomments', 'pilot comments'],
};

/** Finds the Flights-table header among any preamble rows ForeFlight's real export puts before it. */
function findHeaderRowIndex(csvRows: readonly (readonly string[])[]): number {
  return csvRows.findIndex((row) => {
    const columns = mapColumns(row, { date: SYNONYMS.date, aircraftId: SYNONYMS.aircraftId });
    return columns.date !== -1 && columns.aircraftId !== -1;
  });
}

function cell(row: readonly string[], index: number): string {
  return index === -1 ? '' : (row[index] ?? '');
}

function hoursOrZero(raw: string): number {
  if (raw.trim() === '') {
    return 0;
  }
  return parseDurationHours(raw) ?? 0;
}

function intOrZero(raw: string): number {
  const parsed = Number.parseInt(raw.trim(), 10);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function importForeFlightCsv(text: string): ImportParseResult {
  const csvRows = parseCsv(text);
  const headerIndex = findHeaderRowIndex(csvRows);
  if (headerIndex === -1) {
    return {
      rows: [],
      errors: [
        {
          line: 1,
          message: 'No ForeFlight Flights table found. Expected a "Date" and "AircraftID" column.',
        },
      ],
    };
  }
  const header = csvRows[headerIndex];
  if (header === undefined) {
    return { rows: [], errors: [] };
  }
  const columns = mapColumns(header, SYNONYMS);

  const rows: ParsedLogbookRow[] = [];
  const errors: ImportParseResult['errors'] = [];

  csvRows.slice(headerIndex + 1).forEach((row, index) => {
    const line = headerIndex + index + 2;
    const date = cell(row, columns.date).trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      // ForeFlight's own template rows below the header describe units, not
      // flights — skipped quietly rather than reported as an error, the same
      // way `parseCsv` treats a trailing blank line as not a row.
      if (date !== '') {
        errors.push({ line, message: `"${date}" is not a date in YYYY-MM-DD form.` });
      }
      return;
    }

    const nightHr = hoursOrZero(cell(row, columns.night));
    const totalHr = hoursOrZero(cell(row, columns.totalTime));
    const dayHr = Math.max(0, totalHr - nightHr);

    rows.push({
      date,
      ...(cell(row, columns.aircraftId).trim() === ''
        ? {}
        : { registration: cell(row, columns.aircraftId).trim().toUpperCase() }),
      ...(cell(row, columns.from).trim() === '' ? {} : { from: cell(row, columns.from).trim() }),
      ...(cell(row, columns.to).trim() === '' ? {} : { to: cell(row, columns.to).trim() }),
      ...intermediateFrom(cell(row, columns.route)),
      ...(cell(row, columns.pilotComments).trim() === ''
        ? {}
        : { remarks: cell(row, columns.pilotComments).trim() }),
      approaches: blankApproaches(),
      time: {
        ...blankTimeMatrix(),
        dayHr,
        nightHr,
        picHr: hoursOrZero(cell(row, columns.pic)),
        coPilotHr: hoursOrZero(cell(row, columns.sic)),
        dualHr: hoursOrZero(cell(row, columns.dualReceived)),
        instructorHr: hoursOrZero(cell(row, columns.dualGiven)),
        actualInstrumentHr: hoursOrZero(cell(row, columns.actualInstrument)),
        simulatedInstrumentHr: hoursOrZero(cell(row, columns.simulatedInstrument)),
        groundInstrumentHr: hoursOrZero(cell(row, columns.simulatedFlight)),
      },
      landings: {
        ...blankLandings(),
        day: intOrZero(cell(row, columns.dayLandingsFullStop)),
        night: intOrZero(cell(row, columns.nightLandingsFullStop)),
      },
      takeoffs: {
        ...blankTakeoffs(),
        day: intOrZero(cell(row, columns.dayTakeoffs)),
        night: intOrZero(cell(row, columns.nightTakeoffs)),
      },
    });
  });

  return { rows, errors };
}
